<template>
	<v-container>
		<v-row class="contentRow" v-if="loading">
			<v-col class="text-center">
				<v-progress-circular indeterminate color="primary"></v-progress-circular>
				<p class="mt-4">Loading transaction details...</p>
			</v-col>
		</v-row>
		<v-row class="contentRow" v-else-if="error">
			<v-col>
				<v-alert type="error" prominent>
					<v-alert-title>Error loading transaction</v-alert-title>
					{{ error }}
				</v-alert>
			</v-col>
		</v-row>
		<v-row class="contentRow" v-else-if="dataVisible">
			<v-col>
				<h1 class="mb-2">{{sentOrRecieved}} Transaction #{{ data.Id }}</h1>
				<!--                Status Section-->
				<v-banner
					style="border-bottom: hidden"
					class="rounded my-1"
					color="warning lighten-3"
					light
					v-if="data.ReplacedTransaction"
				>
					<v-row class="ml-1">
						<span>
							<strong class="ml-1">Warning! </strong>This transaction has been
							edited, and replaced by transaction
							<strong>
								<router-link
									:to="`/trace/transactions/${data.ReplacedTransaction}`"
									style="color: inherit; text-decoration: none"
								>
									#{{ data.ReplacedTransaction }}</router-link
								> </strong
							>!
						</span>
					</v-row>
				</v-banner>
				<v-banner
					class="rounded my-2"
					style="border-bottom: hidden"
					light
					v-if="data.ReplacingTransaction"
					outlined
				>
					<v-row class="ml-1">
						<span>
							<strong class="ml-1">Note: </strong>
							This transaction edited, and replaced transaction
							<strong>
								<router-link
									:to="`/trace/transactions/${data.ReplacingTransaction}`"
									style="color: inherit; text-decoration: none"
								>
									#{{ data.ReplacingTransaction }}</router-link
								> </strong
							>!
						</span>
					</v-row>
				</v-banner>
				<v-banner
					v-if="data.Status"
					:style="{ borderColor: data.Status.color, borderBottom: 'hidden' }"
					class="rounded my-2"
					outlined
				>
					<v-row class="ml-1">
						<h4 class="ml-1">Status:</h4>
						<v-tooltip top>
							<template v-slot:activator="{ on, attrs }">
								<p
									class="mx-2 my-0"
									:style="{ color: data.Status.color }"
									v-bind="attrs"
									v-on="on"
								>
									<v-icon small :style="{ color: data.Status.color }"
										>fas fa-{{ data.Status.icon }}</v-icon
									>
									{{ data.Status.description_short }}
								</p>
							</template>
							<span>{{ data.Status.description_long }}</span>
						</v-tooltip>
					</v-row>
					<v-row class="ml-1" v-if="data.Status.reason">
						<h4 class="ml-1">Reason:</h4>
						<p class="mx-2" style="margin-bottom: 0px">
							{{ data.Status.reason }}
						</p>
					</v-row>
				</v-banner>
				<!--                Button Section-->
				<v-col>
					<v-row>
						<div
							v-for="action in data.AvailableActions"
							v-bind:key="action.action"
						>
							<v-tooltip top>
								<template v-slot:activator="{ on, attrs }">
									<v-badge
										color="error"
										overlap
										:content="action.badge"
										v-if="action.badge"
									>
										<v-btn
											v-bind="attrs"
											v-on="on"
											:color="action.color === 'neutral' ? 'primary' : null"
											:style="{
												backgroundColor: action.color,
												margin: '5px',
												color: 'white',
											}"
											v-if="!action.settable"
											:href="action.link.replace('::id::', transactionID)"
											target="_blank"
										>
											<v-icon small left>fas fa-{{ action.icon }}</v-icon>
											{{ action.action }}
										</v-btn>
									</v-badge>
									<v-btn
										v-bind="attrs"
										v-on="on"
										:color="action.color === 'neutral' ? 'primary' : null"
										:style="{
											backgroundColor: action.color,
											margin: '5px',
											color: 'white',
										}"
										v-else-if="!action.settable && action.action == 'Print'"
										@click="downloadPDF(action.link)"
										target="_blank"
									>
										<v-icon small left>fas fa-{{ action.icon }}</v-icon>
										{{ action.action }}
									</v-btn>
									<v-btn
										v-bind="attrs"
										v-on="on"
										:color="action.color === 'neutral' ? 'primary' : null"
										:style="{
											backgroundColor: action.color,
											margin: '5px',
											color: 'white',
										}"
										v-else-if="!action.settable"
										:href="action.link.replace('::id::', transactionID)"
										target="_blank"
									>
										<v-icon small left>fas fa-{{ action.icon }}</v-icon>
										{{ action.action }}
									</v-btn>
									<v-btn
										v-bind="attrs"
										v-on="on"
										:style="{
											backgroundColor: action.color,
											margin: '5px',
											color: 'white',
										}"
										v-if="action.settable"
										@click="setActionModal(action)"
									>
										<v-icon small left>fas fa-{{ action.icon }}</v-icon>
										{{ action.action }}
									</v-btn>
								</template>
								<span>{{ action.action_description }}</span>
							</v-tooltip>
						</div>
					</v-row>
				</v-col>

				<!--                Data Section-->
				<v-simple-table>
					<template v-slot:default>
						<tbody>
							<tr>
								<td>Timestamp</td>
								<td>{{ data.Timestamp | moment("DD.MM.YYYY  h:mm:ss") }}</td>
							</tr>
							<tr>
								<td>Hash</td>
								<td>
									{{ data.Hash }}
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-btn
												icon
												v-bind="attrs"
												v-on="on"
												@click="copyToClipboard(data.Hash)"
											>
												<v-icon small color="primary">fas fa-paste</v-icon>
											</v-btn>
										</template>
										<span>{{ copyTooltip }}</span>
									</v-tooltip>
								</td>
							</tr>
							<tr>
								<td>Blockchain Status</td>
								<td v-if="data.BlockchainStatus !== 'Awaiting validification'">
									<a
										:href="'https://nodes.orixe.tech/blocks/' + data.BlockchainStatus" target='_blank'>Block #{{data.BlockchainStatus}}</a>
									
								</td>
								<td v-if="data.BlockchainStatus === 'Awaiting validification'">
									{{ data.BlockchainStatus }}
								</td>
							</tr>
							<tr v-if="data.Sender">
								<td>Sender</td>
								<td>{{ data.Sender.id }}</td>
							</tr>
							<tr v-if="data.Recipient">
								<td>Recipient</td>
								<td>{{ data.Recipient.id }}</td>
							</tr>
							<tr v-for="item in (data.data || []).filter(item => item.name !== 'PreviousTransactions')" :key="item.name">
								<td>
									{{ item.name.replace(/([a-z])([A-Z])/g, "$1 $2").trim() }}
									<v-chip
										v-if="item.replaced"
										class="ml-2"
										label
										small
										color="grey"
									>
										<h4>Edited</h4>
									</v-chip>
								</td>
								<template v-if="!item.replaced">
									<td v-if="item.name === 'ChainID'">
										<router-link
											:to="'/trace/transactions/' + transactionID + '/chain'"
											style="color: inherit"
											>{{ item.value }}</router-link
										>
									</td>
									<td
										v-else-if="item.type && item.type.indexOf('file') >= 0 && !!item.value"
									>
										<div v-if="item.value" class="my-3">
											<div
												class="downloadFile mb-3"
												v-for="(file, index) in item.value.split(',')"
												v-bind:key="file"
											>
												<v-avatar tile class="mr-3" size="28" color="secondary">
													<h3 class="white--text">{{ index + 1 }}</h3>
												</v-avatar>
												<v-btn
													tile
													elevation="0"
													small
													v-if="file"
													color="primary"
													@click="downloadFile(file, item.name)"
													>Download File</v-btn
												>
											</div>
										</div>
									</td>
									<td v-else-if="item.name !== 'ChainID'">{{ item.value }}</td>
								</template>
								<template v-else-if="item.replaced">
									<td>
										<v-row>
											<v-chip
												v-if="item.replaced"
												class="ml-2"
												label
												small
												color="grey lighten-1"
											>
												{{ item.previousValue }}
											</v-chip>
											<v-icon small right color="black"
												>fas fa-angle-double-right</v-icon
											>
											<v-chip
												v-if="item.replaced"
												class="ml-2"
												label
												small
												color="success"
											>
												<router-link
													v-if="item.name === 'ChainID'"
													:to="
														'/trace/transactions/' + transactionID + '/chain'
													"
													style="color: inherit"
													>{{ item.value }}</router-link
												>
												<template v-if="item.name !== 'ChainID'">{{
													item.value
												}}</template>
											</v-chip>
										</v-row>
									</td>
								</template>
							</tr>
						</tbody>
					</template>
				</v-simple-table>
				<!--                Button Section-->
				<v-col>
					<v-row>
						<div
							v-for="action in data.AvailableActions || []"
							v-bind:key="action.action"
						>
							<v-tooltip top>
								<template v-slot:activator="{ on, attrs }">
									<v-badge
										color="error"
										overlap
										:content="action.badge"
										v-if="action.badge"
									>
										<v-btn
											v-bind="attrs"
											v-on="on"
											:color="action.color === 'neutral' ? 'primary' : null"
											:style="{
												backgroundColor: action.color,
												margin: '5px',
												color: 'white',
											}"
											v-if="!action.settable"
											:href="action.link.replace('::id::', transactionID)"
											target="_blank"
										>
											<v-icon small left>fas fa-{{ action.icon }}</v-icon>
											{{ action.action }}
										</v-btn>
									</v-badge>
									<v-btn
										v-bind="attrs"
										v-on="on"
										:color="action.color === 'neutral' ? 'primary' : null"
										:style="{
											backgroundColor: action.color,
											margin: '5px',
											color: 'white',
										}"
										v-if="!action.settable && !action.badge"
										:href="action.link.replace('::id::', transactionID)"
										target="_blank"
									>
										<v-icon small left>fas fa-{{ action.icon }}</v-icon>
										{{ action.action }}
									</v-btn>
									<v-btn
										v-bind="attrs"
										v-on="on"
										:style="{
											backgroundColor: action.color,
											margin: '5px',
											color: 'white',
										}"
										v-if="action.settable"
										@click="setActionModal(action)"
									>
										<v-icon small left>fas fa-{{ action.icon }}</v-icon>
										{{ action.action }}
									</v-btn>
								</template>
								<span>{{ action.action_description }}</span>
							</v-tooltip>
						</div>
					</v-row>
				</v-col>
			</v-col>
		</v-row>
		<!--        Rescind Modal-->
		<v-dialog v-model="actionModal" max-width="500px">
			<v-card>
				<v-card-title
					><v-icon :color="selectedAction.color" small left
						>fas fa-{{ selectedAction.icon }}</v-icon
					>{{ selectedAction.action }}</v-card-title
				>
				<v-card-subtitle class="my-2">
					Are you sure you want to {{ selectedAction.action }} ?
				</v-card-subtitle>
				<v-card-text v-if="selectedAction.action !== 'Confirm'">
					<v-textarea
						align="center"
						label="Reason"
						placeholder="Reason for status change..."
						outlined
						v-model="actionValue"
					></v-textarea>
				</v-card-text>
				<v-card-actions>
					<v-btn
						class="white--text"
						:color="selectedAction.color"
						@click="actionTransaction"
						>{{ selectedAction.action }}</v-btn
					>
					<v-btn color="grey" text @click="actionModal = false" outlined
						>Dismiss</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import { mapState } from "vuex"
import TransactionAPI from "../../api/trace/transaction"
import LoadingState from "../../consts/LoadingState"
import FilesAPI from "../../api/files/files"
export default {
	name: "TransactionDetailsView",

	data() {
		return {
			copyTooltip: "Copy to clipboard",
			actionModal: false,
			actionValue: null,
			selectedAction: {
				action: null,
				color: null,
				icon: null,
			},
			transactionID: null,
			data: null,
			sentOrRecieved: null,
			error: null
		}
	},
	mounted() {
		if (this.$route.params.id) {
			this.transactionID = this.$route.params.id
			this.getTransaction(this.$route.params.id)
		}
	},
	methods: {
		copyToClipboard(text) {
			navigator.clipboard
				.writeText(text)
				.then(() => {
					this.copyTooltip = "Copied to clipboard"
				})
				.catch(() => {
					this.copyTooltip = "Can't copy to clipboard"
				})
		},
		getTransaction(transactionID) {
			this.error = null
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			TransactionAPI.getTransaction(transactionID)
				.then((data) => {
					if (!data) {
						throw new Error("Transaction data is empty")
					}
					this.data = data
					this.sentOrRecieved = this.$store.getters["user/getUser"].clientID == data.Recipient?.id
						? "Recieved"
						: "Sent"
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.error = error?.message || error || "Failed to load transaction"
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		actionTransaction() {
			this.actionModal = false
			TransactionAPI.editAction(this.transactionID, this.selectedAction.code, {
				action: this.selectedAction.code,
				statusReason: this.actionValue,
			})
				.then(() => {
					this.getTransaction(this.transactionID)
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Transaction was successfully updated!",
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		setActionModal(action) {
			this.actionModal = true
			this.selectedAction = action
		},
		downloadFile(fileID, name) {
			FilesAPI.download("" + fileID, name)
		},
		downloadPDF(rawLink) {
			TransactionAPI.downloadPDF(rawLink, this.transactionID)
				.then( ()  => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Transaction PDF was successfully downloaded!",
						{ root: true }
					)				})
				.catch((err) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{ root: true }
					)
				})
		},
	},
	computed: {
		...mapState("loading", {
			loading: (state) => state.loading == LoadingState.LOADING,
		}),
		dataVisible() {
			return !!this.data
		},
	},
}
</script>
